import React, { useState } from 'react';
import { 
  Download, 
  Eye, 
  FileText, 
  Image, 
  File, 
  Users, 
  Lock, 
  Globe, 
  Award, 
  Trash2,
  MoreHorizontal,
  Filter
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { useToast } from '@/hooks/use-toast';
import { useJobFiles, JobFile } from '@/hooks/useJobFiles';
import { formatBytes } from '@/lib/utils';

interface JobFileViewerProps {
  jobId: string;
  userRole: 'client' | 'vendor';
  className?: string;
}

interface FileModalProps {
  file: JobFile | null;
  open: boolean;
  onClose: () => void;
  onDownload: (file: JobFile) => void;
}

const FileModal: React.FC<FileModalProps> = ({ file, open, onClose, onDownload }) => {
  const [downloadUrl, setDownloadUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const { getDownloadUrl } = useJobFiles({ jobId: file?.job_id || '' });

  React.useEffect(() => {
    if (file && open) {
      loadFile();
    }
  }, [file, open]);

  const loadFile = async () => {
    if (!file) return;
    
    setLoading(true);
    try {
      const url = await getDownloadUrl(file);
      setDownloadUrl(url);
    } catch (error) {
      console.error('Error loading file:', error);
    } finally {
      setLoading(false);
    }
  };

  const canPreview = file?.content_type.startsWith('image/') || 
                    file?.content_type === 'application/pdf';

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden">
        <DialogHeader>
          <div className="flex items-center justify-between">
            <DialogTitle className="truncate">
              {file?.original_filename}
            </DialogTitle>
            <Button
              variant="outline"
              size="sm"
              onClick={() => file && onDownload(file)}
            >
              <Download className="h-4 w-4 mr-2" />
              Download
            </Button>
          </div>
        </DialogHeader>

        <div className="flex-1 overflow-auto">
          {loading && (
            <div className="flex items-center justify-center h-64">
              <p>Loading file...</p>
            </div>
          )}

          {!loading && downloadUrl && canPreview && (
            <div className="w-full h-full min-h-[500px]">
              {file?.content_type.startsWith('image/') ? (
                <img 
                  src={downloadUrl} 
                  alt={file.original_filename}
                  className="max-w-full h-auto mx-auto"
                />
              ) : file?.content_type === 'application/pdf' ? (
                <iframe
                  src={downloadUrl}
                  className="w-full h-full min-h-[500px]"
                  title={file.original_filename}
                />
              ) : null}
            </div>
          )}

          {!loading && (!canPreview || !downloadUrl) && (
            <div className="flex flex-col items-center justify-center h-64 text-center">
              <File className="h-16 w-16 text-muted-foreground mb-4" />
              <p className="text-lg font-medium mb-2">Preview not available</p>
              <p className="text-muted-foreground mb-4">
                Click download to view this file
              </p>
              <Button onClick={() => file && onDownload(file)}>
                <Download className="h-4 w-4 mr-2" />
                Download File
              </Button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export const JobFileViewer: React.FC<JobFileViewerProps> = ({
  jobId,
  userRole,
  className = ''
}) => {
  const [selectedFile, setSelectedFile] = useState<JobFile | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [filterCategory, setFilterCategory] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState('');
  
  const { 
    clientFiles, 
    vendorFiles, 
    loading, 
    error, 
    getDownloadUrl, 
    deleteFile,
    logFileAccess 
  } = useJobFiles({ jobId });
  
  const { toast } = useToast();

  const getFileIcon = (contentType: string) => {
    if (contentType.startsWith('image/')) return <Image className="h-5 w-5" />;
    if (contentType === 'application/pdf') return <FileText className="h-5 w-5" />;
    return <File className="h-5 w-5" />;
  };

  const getVisibilityIcon = (visibility: JobFile['visibility_level']) => {
    switch (visibility) {
      case 'private':
        return <Lock className="h-4 w-4 text-red-500" />;
      case 'public':
        return <Globe className="h-4 w-4 text-blue-500" />;
      case 'award_only':
        return <Award className="h-4 w-4 text-yellow-500" />;
      case 'client_visible':
        return <Users className="h-4 w-4 text-green-500" />;
      default:
        return <Lock className="h-4 w-4" />;
    }
  };

  const getVisibilityLabel = (visibility: JobFile['visibility_level']) => {
    switch (visibility) {
      case 'private':
        return 'Private';
      case 'public':
        return 'Public';
      case 'award_only':
        return 'Award Only';
      case 'client_visible':
        return 'Client Visible';
      default:
        return 'Unknown';
    }
  };

  const handleFileClick = async (file: JobFile) => {
    setSelectedFile(file);
    setModalOpen(true);
    await logFileAccess(file.id, 'view');
  };

  const handleDownload = async (file: JobFile) => {
    try {
      const url = await getDownloadUrl(file);
      const link = window.document.createElement('a');
      link.href = url;
      link.download = file.original_filename;
      link.click();
      
      toast({
        title: "Download started",
        description: file.original_filename,
      });
    } catch (error) {
      toast({
        title: "Download failed",
        description: "Failed to download the file",
        variant: "destructive",
      });
    }
  };

  const handleDelete = async (file: JobFile) => {
    try {
      await deleteFile(file.id);
      toast({
        title: "File deleted",
        description: file.original_filename,
      });
    } catch (error) {
      toast({
        title: "Delete failed",
        description: "Failed to delete the file",
        variant: "destructive",
      });
    }
  };

  const filterFiles = (files: JobFile[]) => {
    return files.filter(file => {
      const matchesCategory = filterCategory === 'all' || file.file_category === filterCategory;
      const matchesSearch = searchQuery === '' || 
        file.original_filename.toLowerCase().includes(searchQuery.toLowerCase()) ||
        file.description?.toLowerCase().includes(searchQuery.toLowerCase());
      
      return matchesCategory && matchesSearch;
    });
  };

  const filteredClientFiles = filterFiles(clientFiles);
  const filteredVendorFiles = filterFiles(vendorFiles);

  const FileList = ({ files, title, emptyMessage }: { 
    files: JobFile[], 
    title: string, 
    emptyMessage: string 
  }) => (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          {title}
          <Badge variant="secondary">{files.length}</Badge>
        </CardTitle>
      </CardHeader>
      <CardContent>
        {files.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            <File className="h-12 w-12 mx-auto mb-2 opacity-50" />
            <p>{emptyMessage}</p>
          </div>
        ) : (
          <div className="space-y-2">
            {files.map((file) => (
              <div
                key={file.id}
                className="flex items-center justify-between p-3 border rounded-lg hover:bg-muted/50 transition-colors"
              >
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  {getFileIcon(file.content_type)}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <p className="font-medium truncate">{file.original_filename}</p>
                      <div className="flex items-center gap-1">
                        {getVisibilityIcon(file.visibility_level)}
                        <span className="text-xs text-muted-foreground">
                          {getVisibilityLabel(file.visibility_level)}
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <span>{formatBytes(file.file_size_bytes)}</span>
                      <span>•</span>
                      <span className="capitalize">{file.file_category}</span>
                      <span>•</span>
                      <span>{new Date(file.created_at).toLocaleDateString()}</span>
                    </div>
                    {file.description && (
                      <p className="text-sm text-muted-foreground mt-1">
                        {file.description}
                      </p>
                    )}
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleFileClick(file)}
                  >
                    <Eye className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDownload(file)}
                  >
                    <Download className="h-4 w-4" />
                  </Button>
                  
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="sm">
                        <MoreHorizontal className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent>
                      <DropdownMenuItem onClick={() => handleDelete(file)}>
                        <Trash2 className="h-4 w-4 mr-2" />
                        Delete
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );

  if (loading) {
    return (
      <div className={`text-center py-8 ${className}`}>
        Loading files...
      </div>
    );
  }

  if (error) {
    return (
      <div className={`text-center text-red-600 py-8 ${className}`}>
        {error}
      </div>
    );
  }

  return (
    <div className={className}>
      {/* Filters */}
      <div className="flex items-center gap-4 mb-6">
        <div className="flex items-center gap-2">
          <Filter className="h-4 w-4" />
          <Select value={filterCategory} onValueChange={setFilterCategory}>
            <SelectTrigger className="w-40">
              <SelectValue placeholder="Filter by category" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Categories</SelectItem>
              <SelectItem value="general">General</SelectItem>
              <SelectItem value="specifications">Specifications</SelectItem>
              <SelectItem value="artwork_files">Artwork Files</SelectItem>
              <SelectItem value="deliverables">Deliverables</SelectItem>
              <SelectItem value="contracts">Contracts</SelectItem>
              <SelectItem value="invoices">Invoices</SelectItem>
            </SelectContent>
          </Select>
        </div>
        
        <Input
          placeholder="Search files..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="max-w-xs"
        />
      </div>

      {/* File Lists */}
      <div className="space-y-6">
        <FileList 
          files={filteredClientFiles}
          title="Client Files"
          emptyMessage="No client files uploaded"
        />
        
        <FileList 
          files={filteredVendorFiles}
          title="Vendor Files"
          emptyMessage="No vendor files uploaded"
        />
      </div>

      <FileModal
        file={selectedFile}
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onDownload={handleDownload}
      />
    </div>
  );
};